import type { DocFieldSource, FuelSupplyEvidence, OnSiteInfrastructureData, SiteEvidenceDocument, StructuredEvidenceValue } from '../../types/siteEvidence';
import type { SiteDocument } from '../../types/siteRag';
import { documentBodyText, documentNeedsBodyHydration } from '../documentBody';
import { documentNonAdoptionReason } from './documentAdoption';

export type PipelineProjectionSed = Pick<SiteEvidenceDocument, 'grid' | 'onSiteInfrastructure' | 'fuelSupply' | 'collectedAt'>;
export const UTILITY_PIPELINE_FIELD_KEYS = ['nearestPipelineKm', 'nearestPipelineOperator', 'nearestPipelineMassFlowValue', 'nearestPipelineMassFlowUnit', 'nearestPipelineMassFlowBasis'] as const;
type PipelineKey = typeof UTILITY_PIPELINE_FIELD_KEYS[number];
const FLOW_KEYS = ['nearestPipelineMassFlowValue', 'nearestPipelineMassFlowUnit', 'nearestPipelineMassFlowBasis'] as const;

function receiptIsCurrent(sed: PipelineProjectionSed, key: PipelineKey, source: DocFieldSource): boolean {
  const detail = sed.grid?.provenanceDetail;
  const receipt = detail?.autoFillFieldSources?.[key];
  const { utilityPipelineProjection: _projection, ...originalSource } = source;
  return !!receipt && ['utility-letter', 'utility-service-letter'].includes(receipt.requirementId)
    && detail?.fieldReviews?.[key]?.decision !== 'rejected'
    && detail?.fieldReviews?.[key]?.decision !== 'corrected'
    && detail?.autoFillFields?.[key] === source.value
    && JSON.stringify(receipt) === JSON.stringify(originalSource);
}

/** Immediate read fence: reviewing the original grid extraction does not
 * wait for another document pass before withholding its copied gas capacity.
 * Unmarked provider/KMZ facts and independently changed human facts retain
 * their existing authority. This is a guard, never an evidence upgrade. */
export function utilityPipelineProjectionIsCurrent(sed: PipelineProjectionSed): boolean {
  const section = sed.onSiteInfrastructure, data = section?.data;
  if (!section || !data) return true;
  const flow = data.nearestPipelineMassFlow;
  const reviews = section.provenanceDetail?.fieldReviews;
  const nestedReview = reviews?.nearestPipelineMassFlow?.decision;
  if (nestedReview === 'rejected') return false;
  if (nestedReview === 'corrected') return true;
  const q = section.docFieldSources?.nearestPipelineMassFlowValue;
  const u = section.docFieldSources?.nearestPipelineMassFlowUnit;
  const b = section.docFieldSources?.nearestPipelineMassFlowBasis;
  // A separately replaced canonical nested flow is not the old alias copy.
  if (flow && q?.utilityPipelineProjection && u?.utilityPipelineProjection
    && (flow.value !== q.value || flow.unit !== u.value || flow.basis !== b?.value)) {
    // Checking a copied value is not authority to replace its source tuple.
    return nestedReview !== 'accepted' && !FLOW_KEYS.some(key =>
      reviews?.[key]?.decision === 'accepted' || reviews?.[key]?.decision === 'rejected');
  }
  const values = { nearestPipelineMassFlowValue: flow?.value ?? data.nearestPipelineMassFlowValue,
    nearestPipelineMassFlowUnit: flow?.unit ?? data.nearestPipelineMassFlowUnit,
    nearestPipelineMassFlowBasis: flow?.basis ?? data.nearestPipelineMassFlowBasis };
  for (const key of FLOW_KEYS) {
    const source = section.docFieldSources?.[key];
    if (!source?.utilityPipelineProjection) continue;
    const review = reviews?.[key]?.decision;
    if (review === 'rejected') return false;
    if (review === 'corrected') continue;
    // A replacement has independent authority; an accepted copy still needs
    // the unchanged source value, original receipt and current document marker.
    if (source.value !== values[key]) {
      if (review === 'accepted' || nestedReview === 'accepted') return false;
      continue;
    }
    if (source.utilityPipelineProjection.schemaVersion !== 1
      || source.utilityPipelineProjection.current !== true || !receiptIsCurrent(sed, key, source)) return false;
  }
  return true;
}

/** Revalidate exact copies, including checked document facts. Withdraw only
 * unreviewed copies; accepted display values survive with invalid markers.
 * The original grid ledger and independently authored/provider values survive. */
function revalidateOwnedCopies(sed: PipelineProjectionSed, docs: ReadonlyMap<string, SiteDocument>): string[] {
  const section = sed.onSiteInfrastructure, data = section?.data;
  if (!section || !data) return [];
  const sources = section.docFieldSources ?? {};
  const reviews = section.provenanceDetail?.fieldReviews;
  const owned = (key: PipelineKey) => !!sources[key]?.utilityPipelineProjection
    && sources[key]!.value === data[key] && reviews?.[key]?.decision !== 'corrected';
  const bad = new Set<PipelineKey>();
  for (const key of UTILITY_PIPELINE_FIELD_KEYS) {
    const source = sources[key];
    if (!source?.utilityPipelineProjection || !owned(key)) continue;
    const doc = docs.get(source.docId);
    const fields = sed.grid?.provenanceDetail?.autoFillFields;
    const current = source.utilityPipelineProjection.schemaVersion === 1 && receiptIsCurrent(sed, key, source)
      && !!doc && !documentNonAdoptionReason('onSiteInfrastructure', source) && !!fields
      && sourceSupportedUtilityResourceFields(fields, documentBodyText(doc))[key] === source.value;
    source.utilityPipelineProjection = { schemaVersion: 1, current };
    if (!current) bad.add(key);
  }
  // Losing either member invalidates the machine-owned quantity/unit pair.
  if (bad.has('nearestPipelineMassFlowValue') || bad.has('nearestPipelineMassFlowUnit')) {
    FLOW_KEYS.forEach(key => {
      if (owned(key)) {
        bad.add(key);
        sources[key]!.utilityPipelineProjection = { schemaVersion: 1, current: false };
      }
    });
  }
  const q = sources.nearestPipelineMassFlowValue, u = sources.nearestPipelineMassFlowUnit, b = sources.nearestPipelineMassFlowBasis;
  const nestedOwned = q?.utilityPipelineProjection && u?.utilityPipelineProjection
    && !reviews?.nearestPipelineMassFlow
    && data.nearestPipelineMassFlow?.value === q.value && data.nearestPipelineMassFlow?.unit === u.value
    && data.nearestPipelineMassFlow?.basis === b?.value;
  if (nestedOwned && FLOW_KEYS.some(key => bad.has(key))) delete data.nearestPipelineMassFlow;
  const removed: string[] = [];
  for (const key of bad) {
    // An accepted original extraction remains visible as a human-reviewed
    // fact, but its invalid marker keeps it out of automatic firm capacity.
    if (reviews?.[key] || (FLOW_KEYS.some(flowKey => flowKey === key)
      && reviews?.nearestPipelineMassFlow)
      || sed.grid?.provenanceDetail?.fieldReviews?.[key]?.decision === 'accepted') continue;
    delete data[key]; delete sources[key]; removed.push(key);
  }
  return removed;
}

function refreshFuelPipelineMirror(sed: PipelineProjectionSed): void {
  if (!sed.fuelSupply?.data) return;
  const km = sed.onSiteInfrastructure?.data?.nearestPipelineKm;
  sed.fuelSupply.data = { ...sed.fuelSupply.data,
    nearestPipelineKm: typeof km === 'number' && Number.isFinite(km) ? km : null,
    nearestPipelineOperator: sed.onSiteInfrastructure?.data?.nearestPipelineOperator ?? null,
    pipelineSource: pipelineEvidenceSource(sed.onSiteInfrastructure) };
}

export function sourceSupportedUtilityResourceFields(
  fields: Record<string, StructuredEvidenceValue>,
  body: string,
): Record<string, StructuredEvidenceValue> {
  const statements = body.split(/(?<=[.!?])\s+/).map(statement =>
    statement.trim().replace(/[.!?]$/, '').replace(/(?<=\d),(?=\d{3}\b)/g, ''));
  const escaped = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const qualifier = '(?:(?:assumed|fictional|synthetic|source-stated)\\s+)?';
  const supported = (pattern: string) => statements.some(statement =>
    new RegExp(`^${qualifier}${pattern}$`, 'i').test(statement));
  const resourceFields: Record<string, StructuredEvidenceValue> = {};
  const distanceLabels: Record<string, string> = {
    nearestTransmissionKm: '(?:the\\s+)?nearest\\s+transmission(?:\\s+(?:corridor|line))?(?:\\s+distance)?',
    substationDistKm: '(?:the\\s+)?(?:nearest\\s+)?substation(?:\\s+distance)?',
    nearestPipelineKm: '(?:the\\s+)?nearest\\s+(?:(?:natural\\s+)?gas\\s+)?pipeline(?:\\s+(?:distance|corridor))?',
  };
  for (const [key, label] of Object.entries(distanceLabels)) {
    const value = fields[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0
      && supported(`${label}\\s*(?::|=|is(?:\\s+located)?|located)?\\s*${escaped(String(value))}\\s*(?:km|kilomet(?:er|re)s?)(?:\\s+(?:away|from\\s+(?:the|this)\\s+(?:site|parcel)))?`
        // Delivery uncertainty qualifies a distinct fact; it must not erase
        // an affirmative proximity measurement or establish a delivery basis.
        + (key === 'nearestPipelineKm'
          ? '(?:;\\s*firm\\s+site\\s+delivery\\s+(?:is\\s+)?(?:not\\s+(?:confirmed|verified|contracted)|unknown))?'
          : ''))) {
      resourceFields[key] = value;
    }
  }
  const operator = fields.nearestPipelineOperator;
  if (typeof operator === 'string'
    && supported(`(?:the\\s+)?nearest\\s+(?:gas\\s+)?pipeline\\s+operator\\s*(?::|=|is)\\s*${escaped(operator)}`)) {
    resourceFields.nearestPipelineOperator = operator;
  }
  const value = fields.nearestPipelineMassFlowValue;
  const unit = fields.nearestPipelineMassFlowUnit;
  const basis = fields.nearestPipelineMassFlowBasis;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && typeof unit === 'string') {
    const flowPattern = new RegExp(`^${qualifier}(?:the\\s+)?(?:nearest\\s+)?(?:gas\\s+)?pipeline\\s+(?:mass\\s+flow|throughput|quantity)\\s*(?::|=|is)\\s*${escaped(String(value))}\\s*${escaped(unit)}(?:\\s*\\(([^()]+)\\))?$`, 'i');
    const witness = statements.map(statement => flowPattern.exec(statement)).find(match => match != null);
    if (witness) {
      resourceFields.nearestPipelineMassFlowValue = value;
      resourceFields.nearestPipelineMassFlowUnit = unit;
      if (typeof basis === 'string' && witness[1]?.trim().toLowerCase() === basis.toLowerCase()) {
        resourceFields.nearestPipelineMassFlowBasis = basis;
      }
    }
  }
  return resourceFields;
}

/** Read the actual field origin; a document or authored fact is never labelled KMZ. */
export function pipelineEvidenceSource(section: SiteEvidenceDocument['onSiteInfrastructure']): FuelSupplyEvidence['pipelineSource'] {
  const km = section?.data?.nearestPipelineKm;
  if (section?.status === 'unavailable' || typeof km !== 'number' || !Number.isFinite(km) || km < 0) return 'none';
  if (section?.docFieldSources?.nearestPipelineKm?.value === km) return 'document';
  if (section?.status === 'verified-from-context') return 'authored';
  if (section?.status === 'verified-from-kmz' || (section?.data?.sourceKmzDocIds?.length ?? 0) > 0) return 'kmz';
  return 'site-evidence';
}

/** Pure projection after owner/body hydration. No model/provider call, formal
 * utility-letter fulfillment, Intent adoption, unit conversion or capacity inference. */
export function projectUtilityPipelineDocumentFacts(sed: PipelineProjectionSed, documents: readonly SiteDocument[]): string[] {
  const docs = new Map(documents.filter(doc => doc.includeInSed !== false && !doc.archived && !doc.deletedAt
    && !documentNeedsBodyHydration(doc)).map(doc => [doc.id, doc]));
  const removed = revalidateOwnedCopies(sed, docs);
  if (removed.length) refreshFuelPipelineMirror(sed);
  const detail = sed.grid?.provenanceDetail;
  if (!detail?.autoFillFieldSources || !detail.autoFillFields) return removed;
  const candidates = new Map<PipelineKey, { value: string | number; source: DocFieldSource; estimated: boolean }>();
  for (const key of UTILITY_PIPELINE_FIELD_KEYS) {
    const value = detail.autoFillFields[key], source = detail.autoFillFieldSources[key];
    if (!source || !['utility-letter', 'utility-service-letter'].includes(source.requirementId)
      || detail.fieldReviews?.[key]?.decision === 'rejected' || detail.fieldReviews?.[key]?.decision === 'corrected'
      || !source.docId || source.value !== value
      || !Number.isFinite(source.confidence) || source.confidence < 0 || source.confidence > 1
      || (typeof value !== 'number' && typeof value !== 'string')) continue;
    const doc = docs.get(source.docId);
    if (!doc || documentNonAdoptionReason('onSiteInfrastructure', source)) continue;
    const body = documentBodyText(doc);
    const supported = sourceSupportedUtilityResourceFields(detail.autoFillFields, body);
    if (supported[key] !== value) continue;
    candidates.set(key, { value, source: { ...source, value },
      estimated: !!doc.demonstrationProvenance || /\b(?:assumed|fictional|synthetic)\b/i.test(body)
        || (key.startsWith('nearestPipelineMassFlow') && typeof detail.autoFillFields.nearestPipelineMassFlowBasis === 'string'
          && /\b(?:not|no|unconfirmed|unverified|unknown|conditional|proposed|planned|future)\b/i.test(detail.autoFillFields.nearestPipelineMassFlowBasis)) });
  }
  // Quantity/unit/basis are one coupled source fact. Never combine documents.
  const quantity = candidates.get('nearestPipelineMassFlowValue'), unit = candidates.get('nearestPipelineMassFlowUnit'), basis = candidates.get('nearestPipelineMassFlowBasis');
  if (!quantity || !unit || quantity.source.docId !== unit.source.docId) {
    candidates.delete('nearestPipelineMassFlowValue'); candidates.delete('nearestPipelineMassFlowUnit'); candidates.delete('nearestPipelineMassFlowBasis');
  } else if (basis && basis.source.docId !== quantity.source.docId) candidates.delete('nearestPipelineMassFlowBasis');
  const current = sed.onSiteInfrastructure;
  // A stale/withheld/derived section and existing human review must not be
  // converted into verified evidence by filling another leaf.
  if (current && (current.carriedForward || current.provenanceDetail?.derivedFromReport
    || current.status === 'verified-from-report' || current.status === 'anomalous'
    || (current.anomalies?.length ?? 0) > 0)) return removed;
  const data: OnSiteInfrastructureData = current?.data ? { ...current.data } : {
    substations: [], corridors: [], waterFeatures: [], otherPoints: [], sourceKmzDocIds: [], layerCount: 0, parsedAt: sed.collectedAt,
  };
  const sources = { ...current?.docFieldSources };
  const changed: PipelineKey[] = [];
  let estimated = false;
  for (const [key, candidate] of candidates) {
    if (current?.provenanceDetail?.fieldReviews?.[key] || data[key] != null) continue;
    // Existing nested facts also own the corresponding scalar aliases.
    if (key.startsWith('nearestPipelineMassFlow') && (current?.data?.nearestPipelineMassFlow
      || current?.data?.nearestPipelineMassFlowValue != null || current?.data?.nearestPipelineMassFlowUnit != null || current?.data?.nearestPipelineMassFlowBasis != null)) continue;
    // Synthetic/assumed inputs cannot confer capacity authority on an existing
    // otherwise verified section. Retain their original grid receipt instead.
    if (candidate.estimated && current?.data && current.status !== 'unavailable') continue;
    const { value } = candidate;
    if (key === 'nearestPipelineKm' && typeof value === 'number') data.nearestPipelineKm = value;
    else if (key === 'nearestPipelineOperator' && typeof value === 'string') data.nearestPipelineOperator = value;
    else if (key === 'nearestPipelineMassFlowValue' && typeof value === 'number') data.nearestPipelineMassFlowValue = value;
    else if (key === 'nearestPipelineMassFlowUnit' && typeof value === 'string') data.nearestPipelineMassFlowUnit = value;
    else if (key === 'nearestPipelineMassFlowBasis' && typeof value === 'string') data.nearestPipelineMassFlowBasis = value;
    else continue;
    sources[key] = { ...candidate.source, utilityPipelineProjection: { schemaVersion: 1, current: true } };
    changed.push(key); estimated ||= candidate.estimated;
  }
  // Pair admission is atomic even when an existing scalar is already owned.
  if (changed.some(key => key.startsWith('nearestPipelineMassFlow'))) {
    const q = sources.nearestPipelineMassFlowValue, u = sources.nearestPipelineMassFlowUnit;
    if (!q || !u || q.docId !== u.docId || typeof data.nearestPipelineMassFlowValue !== 'number' || typeof data.nearestPipelineMassFlowUnit !== 'string') {
      for (const key of changed.filter(key => key.startsWith('nearestPipelineMassFlow'))) {
        delete data[key]; delete sources[key]; changed.splice(changed.indexOf(key), 1);
      }
    } else {
      data.nearestPipelineMassFlow = { value: data.nearestPipelineMassFlowValue, unit: data.nearestPipelineMassFlowUnit,
        ...(data.nearestPipelineMassFlowBasis ? { basis: data.nearestPipelineMassFlowBasis } : {}) };
    }
  }
  if (!changed.length) return removed;
  const ids = [...new Set(changed.map(key => sources[key]!.docId))];
  const newDocumentSection = !current?.data || current.status === 'unavailable';
  const refs = [...(current?.linkedDocumentRefs ?? [])];
  for (const id of ids) {
    if (refs.some(ref => ref.docId === id)) continue;
    const prior = sed.grid.linkedDocumentRefs?.find(ref => ref.docId === id);
    const source = changed.map(key => sources[key]!).find(source => source.docId === id)!;
    refs.push(prior ? { ...prior } : { docId: id, role: 'supporting', score: source.confidence,
      rationale: 'Requirement-scoped source-supported pipeline extraction; no firm-delivery inference.',
      evidenceQuotes: source.excerptPreview ? [source.excerptPreview] : [],
      ...(source.attribution ? { attribution: source.attribution } : {}),
      ...(source.pageNumber != null ? { pageNumber: source.pageNumber } : {}),
      ...(source.sourceOffset ? { sourceOffset: { ...source.sourceOffset } } : {}) });
  }
  sed.onSiteInfrastructure = {
    ...(current ?? {}), data, status: newDocumentSection ? estimated ? 'anomalous' : 'verified-from-doc' : current?.status ?? 'unavailable',
    source: newDocumentSection ? 'Source-supported user document pipeline facts' : current?.source ?? 'Source-supported user document pipeline facts',
    retrievedAt: current?.retrievedAt ?? sed.collectedAt,
    confidence: newDocumentSection ? Math.min(...changed.map(key => sources[key]!.confidence)) : current?.confidence ?? 0,
    anomalies: estimated ? ['Assumed or fictional pipeline input; not independently verified firm delivery.'] : [...(current?.anomalies ?? [])],
    ...(estimated ? { sourceGrade: 'inference', qualification: 'estimated' } : newDocumentSection ? { qualification: 'verified' } : {}),
    docFieldSources: sources,
    linkedDocumentRefs: refs,
    linkedDocumentIds: [...new Set([...(current?.linkedDocumentIds ?? []), ...ids])],
    provenance: newDocumentSection ? 'doc' : current?.provenance,
    provenanceDetail: { ...(current?.provenanceDetail ?? { mergeReason: 'source-supported-pipeline-projection' }),
      ...(newDocumentSection ? { docId: ids[0] } : {}) },
  };
  // Refresh only the source mirror, preserving the already collected price,
  // heat-rate assumption, provider status and other fuel evidence byte-values.
  refreshFuelPipelineMirror(sed);
  return [...new Set([...removed, ...changed])];
}
